import toastr from 'react-hot-toast';
import { useQuery } from 'react-query';
import { fetchWrapper } from 'utils/fetchWrapper';
import { CACHE_AND_STALE_TIME } from 'constants/index';
import { filterIncludedItem } from 'utilities/hooks/responseHandlers';
import {
  generateIncludeQuery,
  generatePaginationQuery,
  generateSortQuery,
} from 'utilities/hooks/urlGeneration';
import { SPARK_RESOURCE_TYPES } from 'constants/common';
import type { SparkRun, SparkRunResponse } from 'types/SparkRun';
import type { Spark } from 'types/Spark/Spark';
import type { PaginationQuery } from 'utilities/hooks/urlGeneration';
import { ApplicationUser } from 'types/ApplicationUser';
import React from 'react';

export type SparkRunsHistoryType = {
  applicationUser: ApplicationUser | undefined;
  spark: Spark | undefined;
} & SparkRun;

const getUrl = ({
  marketplaceAccountId,
  paginationQuery,
  statusFilter,
  runIdFilter,
}: {
  marketplaceAccountId: string;
  paginationQuery?: PaginationQuery;
  statusFilter?: string;
  runIdFilter?: string;
}): string => {
  let url = `${SPARK_RESOURCE_TYPES.SPARK_RUN}?`;
  
  // Construct filter components
  const filterComponents = [];
  
  // Always include marketplace account ID filter
  filterComponents.push(`equals(spark.marketplaceAccountID,%27${marketplaceAccountId}%27)`);
  
  // Add status filter if provided
  if (statusFilter && statusFilter !== 'all') {
    filterComponents.push(`equals(sparkRunStatusTypeID,%27${statusFilter}%27)`);
  }
  
  // Add run ID filter if provided
  if (runIdFilter) {
    filterComponents.push(`equals(id,%27${runIdFilter}%27)`);
  }
  
  // Combine filter components
  if (filterComponents.length === 1) {
    url += `filter=expr:${filterComponents[0]}`;
  } else {
    url += `filter=expr:and(${filterComponents.join(',')})`;
  }
  
  // Add includes
  url += `&${generateIncludeQuery(['spark', 'applicationUser'])}`;
  
  // Add sorting
  url += `&${generateSortQuery('runEnd', 'Decs')}`;
  
  // Add pagination if provided
  if (paginationQuery) {
    url += `&${generatePaginationQuery(paginationQuery)}`;
  }
  
  return url;
};

const getSparkRun = ({
  marketplaceAccountId,
  paginationQuery,
  statusFilter,
  runIdFilter,
}: {
  marketplaceAccountId: string;
  paginationQuery?: PaginationQuery;
  statusFilter?: string;
  runIdFilter?: string;
}) => {
  if (!marketplaceAccountId) {
    return Promise.reject('Marketplace account ID not found');
  }
  
  return fetchWrapper<SparkRunResponse>({
    url: getUrl({
      marketplaceAccountId,
      paginationQuery,
      statusFilter,
      runIdFilter,
    }),
    method: 'GET',
  });
};

export function useMarketplaceSparkRuns({
  marketplaceAccountId,
  pagination,
  statusFilter,
  runIdFilter,
}: {
  marketplaceAccountId?: string;
  pagination?: PaginationQuery;
  statusFilter?: string;
  runIdFilter?: string;
}) {
  const pageNumber = pagination?.pageNumber;

  const response = useQuery(
    [
      'marketplaceSparkRun',
      marketplaceAccountId,
      statusFilter,
      runIdFilter,
      pageNumber,
    ],
    () =>
      getSparkRun({
        marketplaceAccountId: marketplaceAccountId || '',
        paginationQuery: pagination,
        statusFilter,
        runIdFilter,
      }),
    {
      onError: (error) => {
        if (error instanceof Error) {
          toastr.error(`Failed to fetch Marketplace Spark Runs: ${error.message}`);
        }
      },
      enabled: !!marketplaceAccountId,
      ...CACHE_AND_STALE_TIME,
    }
  );

  const { data } = response;
  const { data: sparkRuns, included } = data || {};

  const sparks = filterIncludedItem<Spark>(
    included,
    SPARK_RESOURCE_TYPES.SPARKS
  );

  const applicationUsers = filterIncludedItem<ApplicationUser>(
    included,
    SPARK_RESOURCE_TYPES.APPLICATION_USERS
  );
  
  const getApplicationUserByID = (applicationUserID: string) => {
    return applicationUsers?.find((user) => user.id === applicationUserID);
  };

  const sparkRunsHistory: SparkRunsHistoryType[] | undefined = sparkRuns?.map(
    (sparkRun) => {
      return {
        ...sparkRun,
        applicationUser: getApplicationUserByID(sparkRun.attributes.createdBy),
        spark: sparks?.find(
          (spark) => spark.id === sparkRun.attributes.sparkID
        ),
      };
    }
  );
  
  const total = React.useMemo(() => {
    return data?.meta?.total;
  }, [data?.meta?.total]);

  return {
    sparkRuns,
    sparkRunsHistory,
    getApplicationUserByID,
    total,
    ...response,
  };
} 